import {
  IframeLayout,
  SeparatorImg,
  SeparatorMoon,
  Box,
  Title,
  Image,
  ProcessorType,
  CreditCardIFrameMethod,
  CreditCardIFrameEvent,
  chooseColor,
  ConnectionTypes,
  FlexiblePaymentMethods,
  SupportedLanguages,
  CreditCardIFrameLanguage,
} from 'vopay';
import { FunctionComponent, useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../providers/Layout.provider';
import { useAccessTokenFields } from '../../../providers/AccessToken.provider';
import { useToken } from '../../../providers/Token.provider';
import { usePaylink, Flow } from '../../../providers/Paylink.provider';
import usePostPaylinkSetAccount from '../../../hooks/useApi/usePostPaylinkSetAccount';
import usePostLinkCreditCard from '@/hooks/useApi/usePostLinkCreditCard';
import usePostLinkPushToCard from '@/hooks/useApi/usePostLinkPushToCard';
import styles from './CreditCardFrame.module.scss';
import useDataFetchIndicator from '../../../hooks/useDataFetchIndicator';
import { useFlexiblePayment } from '@/providers/FlexiblePayment.provider';
import ConnectCreditCardContext, { ConnectCreditCardCtx } from '../CreditCard/ConnectCreditCardContext';
import ConnectCreditCardDialogue from '../CreditCard/Dialogue/ConnectCreditCardDialogue';
import { ConnectCreditCardDialogueHandler, DialogueHandlerProps } from '../CreditCard//types';
import BrandingLogo from '@/components/UI/BrandingLogo/BrandingLogo';
import useFetchiQ11AccessTokenInfo from '@/hooks/useApi/useFetchiQ11AccessTokenInfo';
import useStorage from '@/hooks/useStorage';
import { useConvenienceFeesProvider } from '@/providers/ConvenienceFees.provider';
import { LanguageSwitcher } from 'UI';

type CreditCard = {
  CardNumber: string;
  CardHolderName: string;
  CardBrand: string;
  CardExpiryYear: string;
  CardExpiryMonth: string;
  Address1?: string;
  Address2?: string;
  City?: string;
  Province?: string;
  PostalCode?: string;
  Country?: string;
  Token: string;
  ThreeDSReference?: string;
  ThreeDSProvider?: string;
};

type FC = FunctionComponent;

const CreditCardFrame: FC = () => {
  const baseUrlCreditCard = import.meta.env.VITE_CREDIT_CARD_BASE_URL || '';

  const { i18n, t } = useTranslation('translations', { keyPrefix: 'CreditCardForm' });
  const history = useHistory();
  const { Token, paylink, flow } = usePaylink();
  const { token, isSetByBrowser, iQ11ForOnboarding } = useToken();
  const { paymentMethod, processor, accessToken, extraInfo } = useAccessTokenFields();

  const { layout, color, src, height, positionX, positionY, hasBackButton, hasChangeLanguage, setLayoutInfo } =
    useLayout();

  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [paylinkAccountResponse, setPaylinkAccount] = usePostPaylinkSetAccount({});
  const [shouldConnectCreditCard, setShouldConnectCreditCard] = useState(false);
  const [connectCreditCardError, setConnectCreditCardError] = useState('');
  const [connectCreditCardErrorTitle, setConnectCreditCardErrorTitle] = useState<string | undefined>(undefined);
  const [showConnectCreditCardErrorAlert, setShowConnectCreditCardErrorAlert] = useState(false);
  const [showConnectCreditCardFailAlert, setShowConnectCreditCardFailAlert] = useState(false);
  const [creditCardPropsSaved, setCreditCardPropsSaved] = useState<CreditCard>();
  const [, , , postLinkCreditCard] = usePostLinkCreditCard({});
  const [, , , postLinkPushToCard] = usePostLinkPushToCard({});

  const { paymentMethods, paymentType, addPaymentMethod, isFlexiblePayment, partialPaymentValues, installmentDetails } =
    useFlexiblePayment();

  const { showFees, convenienceFees } = useConvenienceFeesProvider();
  const [, fetchAccessToken] = useFetchiQ11AccessTokenInfo({});
  const storage = useStorage();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const processorMap: Record<string, ProcessorType> = {
    globalpay: 'globalpay',
    worldpay: 'worldpay',
    pci: 'pci',
  };

  // Determine if this is a combined credit/debit card flow
  const isCombinedCardFlow = useMemo(() => {
    // Check if both credit and debit are enabled in the payment selection method
    const paymentSelectionMethod = token?.PaymentSelectionMethod || '';
    const hasCredit = paymentSelectionMethod === 'any' || paymentSelectionMethod.includes('credit');
    const hasDebit = paymentSelectionMethod === 'any' || paymentSelectionMethod.includes('debitcard');
    const cardTypeValidationDisabled = token?.CardTypeValidation === false;

    return hasCredit && hasDebit && cardTypeValidationDisabled && paymentMethod === 'credit';
  }, [token?.PaymentSelectionMethod, token?.CardTypeValidation, paymentMethod]);

  const showBackButton = !(isSetByBrowser && token?.PaymentSelectionMethod === 'credit');

  const postMessageToIframe = (message: CreditCardIFrameMethod | CreditCardIFrameLanguage) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(message, '*');
    }
  };

  const language: SupportedLanguages = i18n.language === 'fr' ? 'fr' : 'en';

  const handleMessage = async (event: MessageEvent<CreditCardIFrameEvent>) => {
    // Check if the message is the trigger to submit the form
    if (!event.data) return;
    const { type } = event.data;

    if (type === 'READY_FOR_MESSAGES') {
      const enabled3ds = !!Token && flow === Flow.FUND__SINGLE && !!token?.Trigger3DS;

      let appID = '';
      let appKey = '';

      if (enabled3ds) {
        const IframeKey = storage.token.getIQ11Token().iQ11Token;

        setDataFetchingInfo(true);
        const ForOnboarding = storage.token.getIQ11Token().iQ11ForOnboarding ?? false;
        const accessToken = await fetchAccessToken({ IframeKey, PaymentSelectionMethod: '3ds', ForOnboarding });
        appID = accessToken.ExtraInfo.AppID || '';
        appKey = accessToken.ExtraInfo.AppKey || '';
        setDataFetchingInfo(false);
      }

      postMessageToIframe({
        type: 'CONFIG',
        payload: {
          language,
          primaryColor: token?.BrandingDetails?.Branding?.PrimaryColor,
          cardType: paymentMethod === 'credit' ? 'credit' : 'debit',
          processorType: processorMap[processor] || 'pci',
          accessToken: accessToken,
          userId: extraInfo.PCIUserID ?? '',
          username: processor === 'worldpay' && extraInfo.Username ? extraInfo.Username : '',
          password: processor === 'worldpay' && extraInfo.Password ? extraInfo.Password : '',
          merchantId: processor === 'worldpay' && extraInfo.MerchantID ? extraInfo.MerchantID : '',
          enabled3ds,
          acceptedCardBrands: token?.AcceptedCardBrands || [],
          cardTypeValidationEnabled: token?.CardTypeValidation || false,
          appInformation: {
            appID: appID,
            appKey: appKey,
          },
          convenienceFees:
            showFees && flow !== Flow.CONNECT && flow !== Flow.NONE ? convenienceFees || undefined : undefined,
          transactionDetails: Token
            ? {
                amount: paylink.Amount,
                currency: paylink.Currency,
                country: token?.Country || '',
                paymentType:
                  flow === Flow.WITHDRAW__SINGLE || flow === Flow.WITHDRAW__MULTIPLE_PAYMENTS
                    ? 'withdraw'
                    : flow === Flow.FUND__SINGLE || flow === Flow.FUND__SCHEDULED
                      ? 'fund'
                      : 'other',
              }
            : undefined,
        },
      });

      setDataFetchingInfo(false);
    }

    if (type === 'CARD') {
      const {
        success,
        paymentToken,
        cardHolderName,
        cardBrand,
        maskedCardNumber,
        expiryMonth,
        expiryYear,
        Address1,
        Address2,
        City,
        Province,
        PostalCode,
        Country,
        threeDSReference,
        threeDSProvider,
      } = event.data.payload;

      setShouldConnectCreditCard(true);

      if (success) {
        try {
          setDataFetchingInfo(true);

          if (paymentMethod === 'credit') {
            const res = await postLinkCreditCard({
              PaymentToken: paymentToken,
              CardHolderName: cardHolderName,
              MaskedCardNumber: maskedCardNumber,
              CardBrand: cardBrand,
              ExpiryYear: expiryYear.toString(),
              ExpiryMonth: expiryMonth.toString(),
              ForOnboarding: iQ11ForOnboarding || false,
              Address1,
              Address2,
              City,
              Province,
              PostalCode,
              Country,
              // 3DS authorization reference (AUT_...) from the card iframe,
              // forwarded so earthnode can pass it to Global Payments as
              // payment_method.authentication.id at transaction time.
              ...(threeDSReference && {
                ThreeDSReference: threeDSReference,
                ThreeDSProvider: threeDSProvider,
              }),
            });

            if (res.Success) {
              setCreditCardPropsSaved({
                CardNumber: maskedCardNumber,
                CardHolderName: cardHolderName,
                CardBrand: cardBrand,
                CardExpiryYear: expiryYear,
                CardExpiryMonth: expiryMonth,
                Token: res.Token,
                Address1,
                Address2,
                City,
                Province,
                PostalCode,
                Country,
                ...(threeDSReference && {
                  ThreeDSReference: threeDSReference,
                  ThreeDSProvider: threeDSProvider,
                }),
              });
            } else {
              setShowConnectCreditCardFailAlert(true);
            }
          } else {
            const res = await postLinkPushToCard({
              PaymentToken: paymentToken,
              CardHolderName: cardHolderName,
              MaskedCardNumber: maskedCardNumber,
              CardBrand: cardBrand,
              ExpiryYear: expiryYear.toString(),
              ExpiryMonth: expiryMonth.toString(),
              Address1,
              Address2,
              City,
              Province,
              PostalCode,
              Country,
            });

            if (res.Success) {
              setCreditCardPropsSaved({
                CardNumber: maskedCardNumber,
                CardHolderName: cardHolderName,
                CardBrand: cardBrand,
                CardExpiryYear: expiryYear,
                CardExpiryMonth: expiryMonth,
                Token: res.Token,
                Address1,
                Address2,
                City,
                Province,
                PostalCode,
                Country,
              });
            } else {
              setShowConnectCreditCardFailAlert(true);
            }
          }
        } finally {
          setDataFetchingInfo(false);
        }
      }
    }

    if (type === 'ERROR') {
      const { errorMessage, errorTitle } = event.data.payload;
      setShowConnectCreditCardErrorAlert(true);
      setConnectCreditCardErrorTitle(errorTitle);
      setConnectCreditCardError(errorMessage);
    }

    if (type === 'LOADING') {
      const { completed } = event.data.payload;
      setDataFetchingInfo(!completed);
    }
  };

  useEffect(() => {
    if (!iframeRef.current) return;

    postMessageToIframe({
      type: 'LANGUAGE_UPDATE',
      payload: { language },
    });
  }, [language]);

  // Attach event listener
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    setDataFetchingInfo(true);

    return () => {
      window.removeEventListener('message', handleMessage);
      setDataFetchingInfo(false);
    };
  }, []);

  useEffect(() => {
    if (Token) {
      setLayoutInfo(
        chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
        'moon',
        '',
        80,
        20,
        0,
        true,
        true,
      );
    } else {
      setLayoutInfo(
        chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
        'moon',
        '',
        0,
        0,
        0,
        showBackButton,
        true,
      );
    }
  }, []);

  const completePaylink = (token: string, paymentMethod: ConnectionTypes) => {
    if (isFlexiblePayment) {
      if (paymentType === FlexiblePaymentMethods.MultiplePaymentMethods) {
        const paymentMethodFind = paymentMethods.find(
          (method) => method.method === ConnectionTypes.DebitCard || method.method === ConnectionTypes.Credit,
        );

        addPaymentMethod({
          ...paymentMethodFind,
          amount: paymentMethodFind?.amount || 0,
          token: token,
          account: creditCardPropsSaved?.CardNumber || '',
          method: paymentMethod,
        });

        history.push('/connect/flexible-payment/multiple-payment-methods');
      }

      if (paymentType === FlexiblePaymentMethods.PartialPayment) {
        setPaylinkAccount({
          SingleUseToken: Token,
          Iq11Token: token,
          PaymentMethod: paymentMethod,
          FlexiblePaymentOptions: {
            PaymentOption: 'partial',
            PartialPaymentDetails: partialPaymentValues,
          },
        });
      }

      if (paymentType === FlexiblePaymentMethods.InstallmentPlan) {
        setPaylinkAccount({
          SingleUseToken: Token,
          Iq11Token: token,
          PaymentMethod: paymentMethod,
          FlexiblePaymentOptions: {
            PaymentOption: 'installments',
            InstallmentDetails: installmentDetails,
          },
        });
      }
    } else {
      setPaylinkAccount({
        SingleUseToken: Token,
        PaymentMethod: paymentMethod,
        Iq11Token: token,
        // The 3DS authentication is bound to the full paylink amount, so it is
        // only forwarded on the plain single-payment path — flexible-payment
        // options charge different amounts and would fail authentication matching.
        ...(creditCardPropsSaved?.ThreeDSReference && {
          ThreeDSReference: creditCardPropsSaved.ThreeDSReference,
          ThreeDSProvider: creditCardPropsSaved.ThreeDSProvider,
        }),
      });
    }
  };

  // 'paylink/set-account'
  useEffect(() => {
    if (shouldConnectCreditCard && creditCardPropsSaved) {
      setShouldConnectCreditCard(false);

      if (Token) {
        completePaylink(
          creditCardPropsSaved.Token,
          paymentMethod === 'credit' ? ConnectionTypes.Credit : ConnectionTypes.DebitCard,
        );
      } else {
        if (isSetByBrowser) {
          const redirectMethod = token?.RedirectMethod;

          const url = `${token?.RedirectURL || ''}?Token=${creditCardPropsSaved?.Token}&PaymentMethod=${
            paymentMethod === 'credit' ? 'creditcard' : 'debitcard'
          }`;

          if (paymentMethod === 'credit') {
            const Response = {
              Step: 'LINK',
              Url: url,
              CreditCardNumber: creditCardPropsSaved?.CardNumber,
              CreditCardHolderName: creditCardPropsSaved?.CardHolderName,
              CreditCardBrand: creditCardPropsSaved?.CardBrand,
              CreditCardExpiryYear: creditCardPropsSaved?.CardExpiryYear,
              CreditCardExpiryMonth: creditCardPropsSaved?.CardExpiryMonth,
              Token: creditCardPropsSaved?.Token,
              PaymentMethod: 'creditcard',
            };

            // inner and outer redirect
            window.top?.postMessage(Response, '*');

            if (window.parent && window.parent !== window.top) {
              window.parent.postMessage(Response, '*');
            }
          } else {
            const Response = {
              Step: 'LINK',
              Url: url,
              CardNumber: creditCardPropsSaved?.CardNumber,
              CardHolderName: creditCardPropsSaved?.CardHolderName,
              CardBrand: creditCardPropsSaved?.CardBrand,
              CardExpiryYear: creditCardPropsSaved?.CardExpiryYear,
              CardExpiryMonth: creditCardPropsSaved?.CardExpiryMonth,
              Token: creditCardPropsSaved?.Token,
              PaymentMethod: 'debitcard',
            };

            // inner and outer redirect
            window.top?.postMessage(Response, '*');

            if (window.parent && window.parent !== window.top) {
              window.parent.postMessage(Response, '*');
            }
          }

          setTimeout(() => {
            if (isSetByBrowser) {
              if (redirectMethod === 'InnerRedirect') {
                window.location.href = url;
              } else if (redirectMethod === 'OuterRedirect') {
                window.top!.location.href = url;
              } else {
                history.push('/success');
              }
            }
          }, 300);
        } else {
          history.push('/dashboard');
        }
      }
    }
  }, [creditCardPropsSaved]);

  const ConnectCreditCard: ConnectCreditCardCtx = {
    showConnectCreditCardSuccessAlert: false,
    showConnectCreditCardErrorAlert,
    showConnectSameCreditCardErrorAlert: false,
    showConnectCreditCardFailAlert,
    connectCreditCardError,
    connectCreditCardErrorTitle,
  };

  useEffect(() => {
    if (!paylinkAccountResponse) return;

    if (paylinkAccountResponse.Success) {
      history.push('/thank-you', {
        paymentMethodType: paymentMethod,
        paymentMethod:
          creditCardPropsSaved?.CardBrand && creditCardPropsSaved?.CardBrand.toLowerCase() !== 'unknown'
            ? `${creditCardPropsSaved.CardBrand} **** ${creditCardPropsSaved.CardNumber?.slice(-4)}`
            : `**** ${creditCardPropsSaved?.CardNumber?.slice(-4)}`,
        cardNumber: creditCardPropsSaved?.CardNumber,
        cardHolderName: creditCardPropsSaved?.CardHolderName,
        cardBrand: creditCardPropsSaved?.CardBrand,
        cardExpiryYear: creditCardPropsSaved?.CardExpiryYear,
        cardExpiryMonth: creditCardPropsSaved?.CardExpiryMonth,
        address: creditCardPropsSaved?.Address1,
        address2: creditCardPropsSaved?.Address2,
        city: creditCardPropsSaved?.City,
        province: creditCardPropsSaved?.Province,
        postalCode: creditCardPropsSaved?.PostalCode,
        country: creditCardPropsSaved?.Country,
      });
    } else {
      setShowConnectCreditCardFailAlert(true);
    }
  }, [paylinkAccountResponse]);

  const handlers = {
    handleConnectCreditCardDialogue: (handlerProps: DialogueHandlerProps<ConnectCreditCardDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: ConnectCreditCardDialogueHandler = {
        create: {
          success: {
            default: () => {},
          },
          fail: {
            default: () => {
              setShowConnectCreditCardErrorAlert(false);
              setConnectCreditCardErrorTitle(undefined);
              setConnectCreditCardError('');
            },
          },
        },
        connect: {
          success: {
            default: () => {
              setShowConnectCreditCardFailAlert(false);
              window.location.reload();
            },
          },
          fail: {
            default: () => {},
          },
        },
        agree: {
          success: {
            default: () => {},
          },
          fail: {
            default: () => {},
          },
        },
      };

      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  return (
    <div style={{ '--right-container-overflow': 'hidden' }}>
      <IframeLayout
        separator={
          layout === 'img' ? (
            <SeparatorImg width={100} height={110} fill={color} />
          ) : layout === 'moon' ? (
            <SeparatorMoon width={140} height={110} fill={color} />
          ) : (
            <></>
          )
        }
        image={
          src === '' ? (
            <></>
          ) : (
            <Image width="auto" height={height} positionX={positionX} positionY={positionY} src={src} />
          )
        }
        hasBackButton={hasBackButton}
        topRight={hasChangeLanguage ? <LanguageSwitcher /> : undefined}
        content={
          <div className={styles.container}>
            {token?.BrandingDetails?.Logo && (
              <div>
                <Box pb="lg" jc="center">
                  <BrandingLogo src={token?.BrandingDetails?.Logo} />
                </Box>
              </div>
            )}

            <Box mb="md" flx="col" height="auto">
              {isCombinedCardFlow && <Title title={`${t('titleCombined')}`} size="lg" bold />}
              {!isCombinedCardFlow && paymentMethod === 'credit' && <Title title={`${t('title')}`} size="lg" bold />}

              {!isCombinedCardFlow && paymentMethod !== 'credit' && (
                <Title title={`${t('titleDebit')}`} size="lg" bold />
              )}
            </Box>

            <ConnectCreditCardContext.Provider value={ConnectCreditCard}>
              <div className={styles.iframeWrapper}>
                <iframe ref={iframeRef} className={styles.cardFrame} src={baseUrlCreditCard} seamless />
                <ConnectCreditCardDialogue handleDialogue={handlers.handleConnectCreditCardDialogue} />
              </div>
            </ConnectCreditCardContext.Provider>
          </div>
        }
      />
    </div>
  );
};

export default CreditCardFrame;
